import { useEffect, useState, useRef } from "react";
import {
  getSAMLConfig,
  setupSAML,
  addEmailDomain,
  deleteEmailDomain,
  createOrganization,
  type Organization,
  type EmailDomain,
  type SAMLConfig,
} from "../../../lib/saml-api";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Loader2,
  Mail,
  CheckCircle,
  AlertTriangle,
  AlertCircle,
} from "lucide-react";
import { auth, firebaseAuth } from "../../../lib/firebase";
import { SAMLGroupMappings } from "./SAMLGroupMappings";
import { useAuth } from "@/contexts/AuthContext";

export function SAMLSettings() {
  const hasLoadedConfig = useRef(false);
  const { user } = useAuth();

  // Data state
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [domains, setDomains] = useState<EmailDomain[]>([]);
  const [config, setConfig] = useState<SAMLConfig | null>(null);
  const [workspaces, setWorkspaces] = useState<{ id: string; name: string }[]>(
    []
  );

  // Form state
  const [enabled, setEnabled] = useState(false);
  const [allowPasswordLogin, setAllowPasswordLogin] = useState(false);
  const [idpEntityId, setIdpEntityId] = useState("");
  const [idpSsoUrl, setIdpSsoUrl] = useState("");
  const [idpCertificate, setIdpCertificate] = useState("");
  const [buttonLabel, setButtonLabel] = useState("Sign in with SAML");

  // Domain management state
  const [newDomain, setNewDomain] = useState("");
  const [addingDomain, setAddingDomain] = useState(false);
  const [deletingDomain, setDeletingDomain] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [domainOwnershipWarning, setDomainOwnershipWarning] = useState<{
    domain: string;
    userDomain: string;
  } | null>(null);

  // UI state
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [creatingOrg, setCreatingOrg] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isPublicDomain, setIsPublicDomain] = useState(false);

  // Email verification state
  const [emailVerified, setEmailVerified] = useState(true);
  const [sendingVerificationEmail, setSendingVerificationEmail] =
    useState(false);
  const [verificationEmailSent, setVerificationEmailSent] = useState(false);

  // Load configuration on mount
  useEffect(() => {
    async function loadConfig() {
      // Prevent double-loading in React Strict Mode
      if (hasLoadedConfig.current) {
        return;
      }
      hasLoadedConfig.current = true;

      try {
        // Force refresh ID token to get latest email_verified status
        const currentUser = firebaseAuth.currentUser;
        if (currentUser) {
          await currentUser.reload(); // Reload user data from Firebase
          await currentUser.getIdToken(true); // Force refresh token with forceRefresh=true
          setEmailVerified(currentUser.emailVerified);
        }

        const data = await getSAMLConfig();

        setOrganization(data.organization);
        setDomains(data.domains);
        setIsPublicDomain(data.is_public_domain);

        if (data.saml) {
          setConfig(data.saml);
          setEnabled(data.saml.enabled);
          setAllowPasswordLogin(data.saml.allow_password_login || false);
          setIdpEntityId(data.saml.idp_entity_id || "");
          setIdpSsoUrl(data.saml.idp_sso_url || "");
          setIdpCertificate(data.saml.idp_certificate || "");
          setButtonLabel(data.saml.button_label || "Sign in with SAML");
        }

        // Load user's workspace for group mappings
        if (currentUser && user?.workspaceId && user?.workspaceName) {
          setWorkspaces([{ id: user.workspaceId, name: user.workspaceName }]);
        }
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : "Failed to load SAML configuration"
        );
      } finally {
        setLoading(false);
      }
    }

    loadConfig();
  }, [user]);

  const handleSave = async () => {
    setError(null);
    setSuccess(null);

    // Validate required fields
    if (!idpEntityId.trim()) {
      setError("Identity Provider Issuer is required");
      return;
    }

    if (!idpSsoUrl.trim()) {
      setError("SAML 2.0 Endpoint is required");
      return;
    }

    if (!idpCertificate.trim()) {
      setError("Public Certificate is required");
      return;
    }

    // Auto-correct certificate format if missing PEM headers
    let finalCertificate = idpCertificate.trim();
    if (
      finalCertificate &&
      finalCertificate.length > 10 &&
      finalCertificate.indexOf("-----BEGIN CERTIFICATE-----") === -1
    ) {
      finalCertificate = `-----BEGIN CERTIFICATE-----\n${finalCertificate}\n-----END CERTIFICATE-----`;
    }

    setSaving(true);

    try {
      const result = await setupSAML({
        idp_entity_id: idpEntityId.trim(),
        idp_sso_url: idpSsoUrl.trim(),
        idp_certificate: finalCertificate,
        button_label: buttonLabel.trim() || "Sign in with SAML",
        enabled,
        allow_password_login: allowPasswordLogin,
      });

      // Show appropriate success message based on whether org was created
      if (result.created_organization) {
        setSuccess("✓ Organization created! SAML SSO has been configured.");
      } else {
        setSuccess("✓ SAML configuration updated successfully.");
      }

      // Reload config
      const data = await getSAMLConfig();
      if (data.saml) {
        setConfig(data.saml);
        // Update form state with saved values
        setEnabled(data.saml.enabled);
        setAllowPasswordLogin(data.saml.allow_password_login || false);
        setIdpEntityId(data.saml.idp_entity_id || "");
        setIdpSsoUrl(data.saml.idp_sso_url || "");
        setIdpCertificate(data.saml.idp_certificate || "");
        setButtonLabel(data.saml.button_label || "Sign in with SAML");
      }
      if (data.organization) {
        setOrganization(data.organization);
        setDomains(data.domains);
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to save SAML configuration"
      );
    } finally {
      setSaving(false);
    }
  };

  const handleAddDomain = async () => {
    if (!newDomain.trim()) {
      setError("Domain is required");
      return;
    }

    // Basic domain validation
    const domainRegex =
      /^[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9]?\.[a-zA-Z]{2,}$/;
    if (!domainRegex.test(newDomain.trim())) {
      setError("Invalid domain format");
      return;
    }

    // Domain ownership warning
    const userEmail = firebaseAuth.currentUser?.email;
    const userDomain = userEmail?.split("@")[1];
    const domainToAdd = newDomain.trim().toLowerCase();

    if (userDomain !== domainToAdd) {
      // Show confirmation dialog
      setDomainOwnershipWarning({
        domain: domainToAdd,
        userDomain: userDomain || "",
      });
      return;
    }

    // If domains match, proceed directly
    await addDomainDirectly(domainToAdd);
  };

  const addDomainDirectly = async (domainToAdd: string) => {
    setAddingDomain(true);
    setError(null);
    setSuccess(null);

    try {
      const addedDomain = await addEmailDomain(domainToAdd);
      setDomains([...domains, addedDomain]);
      setNewDomain("");
      setSuccess(`Domain ${addedDomain.domain} added successfully`);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to add email domain"
      );
    } finally {
      setAddingDomain(false);
    }
  };

  const confirmAddDomain = async () => {
    if (!domainOwnershipWarning) return;

    const domainToAdd = domainOwnershipWarning.domain;
    setDomainOwnershipWarning(null);

    await addDomainDirectly(domainToAdd);
  };

  const cancelAddDomain = () => {
    setDomainOwnershipWarning(null);
  };

  const handleDeleteDomain = (domainId: string, domainName: string) => {
    setDeletingDomain({ id: domainId, name: domainName });
  };

  const confirmDeleteDomain = async () => {
    if (!deletingDomain) return;

    setError(null);
    setSuccess(null);

    try {
      await deleteEmailDomain(deletingDomain.id);
      setDomains(domains.filter((d) => d.id !== deletingDomain.id));
      setSuccess(`Domain ${deletingDomain.name} deleted successfully`);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to delete email domain"
      );
    } finally {
      setDeletingDomain(null);
    }
  };

  const cancelDeleteDomain = () => {
    setDeletingDomain(null);
  };

  const handleCopyToClipboard = (text: string) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        setSuccess("Copied to clipboard!");
        setTimeout(() => setSuccess(null), 2000);
      })
      .catch(() => {
        setError("Failed to copy to clipboard");
      });
  };

  const handleSendVerificationEmail = async () => {
    setSendingVerificationEmail(true);
    setError(null);

    try {
      // Get ID token using the auth wrapper method
      const idToken = await auth.getIdToken();
      if (!idToken) throw new Error("Not authenticated");

      const AUTH_API_URL =
        import.meta.env.VITE_AUTH_API_URL || "http://localhost:9101";

      const response = await fetch(
        `${AUTH_API_URL}/api/auth/email/verify-request`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${idToken}`,
          },
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to send verification email");
      }

      setVerificationEmailSent(true);
      setSuccess(
        "✓ Verification email sent! Please check your inbox and click the verification link."
      );
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to send verification email"
      );
    } finally {
      setSendingVerificationEmail(false);
    }
  };

  const handleCreateOrganization = async () => {
    setCreatingOrg(true);
    setError(null);
    setSuccess(null);

    try {
      await createOrganization();

      // Reload full configuration to get properly formatted data
      const data = await getSAMLConfig();
      setOrganization(data.organization);
      setDomains(data.domains);

      setSuccess(
        "✓ Organization created! You can now configure your IdP with the Service Provider Information below."
      );
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to create organization"
      );
    } finally {
      setCreatingOrg(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Error/Success Messages */}
      {error && (
        <div className="flex items-center gap-2 p-3 rounded-lg border border-destructive/50 bg-destructive/5 text-sm text-destructive">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {success && (
        <div className="flex items-center gap-2 p-3 rounded-lg border border-green-500/30 bg-green-500/5 text-sm text-green-700 dark:text-green-400">
          <CheckCircle className="h-4 w-4 flex-shrink-0" />
          {success}
        </div>
      )}

      {/* Public domain error */}
      {isPublicDomain && (
        <div className="p-4 rounded-lg border border-destructive/30 bg-destructive/5">
          <p className="text-sm font-medium text-destructive mb-1">SAML SSO Not Available</p>
          <p className="text-xs text-muted-foreground">
            SAML SSO is not available for public email domains. Please use a corporate email address to configure SAML SSO for your organization.
          </p>
        </div>
      )}

      {/* No organization yet */}
      {!organization && !isPublicDomain && (
        <div className="p-4 rounded-lg border border-border bg-muted/30">
          <p className="text-sm font-medium text-foreground mb-1">No Organization Yet</p>
          <p className="text-xs text-muted-foreground mb-4">
            Create an organization to get the Service Provider Information (Entity ID & ACS URL) required for configuring your Identity Provider.
          </p>

          {!emailVerified ? (
            <div className="space-y-3">
              <div className="flex items-start gap-2 p-3 rounded-md border border-border bg-card">
                <Mail className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium">Email Verification Required</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Verify your email address before creating an organization.</p>
                </div>
              </div>

              {verificationEmailSent ? (
                <div className="flex items-center gap-2 p-3 rounded-md border border-green-500/30 bg-green-500/5">
                  <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-green-700 dark:text-green-400">Verification email sent</p>
                    <p className="text-xs text-muted-foreground mt-0.5">Check your inbox, then refresh this page.</p>
                  </div>
                  <Button onClick={async () => { const user = firebaseAuth.currentUser; if (user) { await user.reload(); await user.getIdToken(true); } window.location.reload(); }} variant="outline" size="sm">
                    Refresh
                  </Button>
                </div>
              ) : (
                <Button onClick={handleSendVerificationEmail} disabled={sendingVerificationEmail} size="sm">
                  {sendingVerificationEmail && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {sendingVerificationEmail ? "Sending..." : "Send Verification Email"}
                </Button>
              )}
            </div>
          ) : (
            <Button onClick={handleCreateOrganization} disabled={creatingOrg} size="sm">
              {creatingOrg && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {creatingOrg ? "Creating..." : "Create Organization"}
            </Button>
          )}
        </div>
      )}

      {/* Email Domains */}
      {organization && !isPublicDomain && (
        <div className="space-y-3">
          <div>
            <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Email Domains</h3>
            <p className="text-xs text-muted-foreground mt-0.5">Users with these email domains can use SSO</p>
          </div>
          <div className="space-y-1.5">
            {domains.map((domain) => (
              <div key={domain.id} className="flex items-center justify-between p-3 rounded-lg bg-muted/30 border border-border">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-foreground">{domain.domain}</span>
                  {domain.domain === organization.primary_domain && (
                    <span className="text-[10px] bg-muted text-muted-foreground px-1.5 py-0.5 rounded">Primary</span>
                  )}
                </div>
                {domain.domain !== organization.primary_domain && (
                  <Button variant="ghost" size="sm" onClick={() => handleDeleteDomain(domain.id, domain.domain)} className="text-xs text-destructive hover:text-destructive hover:bg-destructive/10 h-7">
                    Delete
                  </Button>
                )}
              </div>
            ))}
          </div>

          {!emailVerified ? (
            <div className="flex items-start gap-2 p-3 rounded-md border border-border bg-card">
              <Mail className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium">Email Verification Required</p>
                <p className="text-xs text-muted-foreground mt-0.5">Verify your email to add domains.</p>
              </div>
              {verificationEmailSent ? (
                <Button onClick={async () => { const user = firebaseAuth.currentUser; if (user) { await user.reload(); await user.getIdToken(true); } window.location.reload(); }} variant="outline" size="sm">Refresh</Button>
              ) : (
                <Button onClick={handleSendVerificationEmail} disabled={sendingVerificationEmail} size="sm">
                  {sendingVerificationEmail ? "Sending..." : "Verify Email"}
                </Button>
              )}
            </div>
          ) : (
            <div className="flex gap-2">
              <Input type="text" value={newDomain} onChange={(e) => setNewDomain(e.target.value)} placeholder="example.com" className="flex-1" disabled={addingDomain} />
              <Button onClick={handleAddDomain} disabled={addingDomain || !newDomain.trim()} size="sm">
                {addingDomain ? "Adding..." : "Add Domain"}
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Service Provider Information */}
      {organization && !isPublicDomain && (
        <div className="space-y-3">
          <div>
            <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Service Provider Information</h3>
            <p className="text-xs text-muted-foreground mt-0.5">Copy these values to your Identity Provider (Azure AD, Okta, etc.)</p>
          </div>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="acs-url" className="text-xs">ACS URL (Single Sign-On URL)</Label>
              <div className="flex gap-2">
                <Input id="acs-url" type="text" value={`https://${import.meta.env.VITE_FIREBASE_AUTH_DOMAIN}/__/auth/handler`} readOnly className="font-mono text-xs bg-muted" />
                <Button variant="outline" size="sm" onClick={() => handleCopyToClipboard(`https://${import.meta.env.VITE_FIREBASE_AUTH_DOMAIN}/__/auth/handler`)}>Copy</Button>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="entity-id" className="text-xs">Entity ID (Audience URI)</Label>
              <div className="flex gap-2">
                <Input id="entity-id" type="text" value={`https://auth.optiprime.com/saml/${organization.id}`} readOnly className="font-mono text-xs bg-muted" />
                <Button variant="outline" size="sm" onClick={() => handleCopyToClipboard(`https://auth.optiprime.com/saml/${organization.id}`)}>Copy</Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* SAML Configuration */}
      <div className="space-y-4">
        <div>
          <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">SAML Configuration</h3>
          <p className="text-xs text-muted-foreground mt-0.5">Configure your SAML SSO settings and Identity Provider details</p>
        </div>

        <div className="space-y-4">
          <div className="flex items-center space-x-2">
            <Checkbox id="enabled" checked={enabled} onCheckedChange={(checked) => setEnabled(checked === true)} disabled={isPublicDomain || !organization} />
            <Label htmlFor="enabled" className="text-sm font-medium cursor-pointer">Enable SAML SSO</Label>
          </div>
          <p className="ml-6 text-xs text-muted-foreground -mt-2">
            {config ? "Uncheck to temporarily disable (configuration preserved)" : "Check this box to configure SAML SSO"}
          </p>

          {(enabled || config) && (
            <>
              <div className="flex items-center space-x-2">
                <Checkbox id="allowPasswordLogin" checked={allowPasswordLogin} onCheckedChange={(checked) => setAllowPasswordLogin(checked === true)} disabled={isPublicDomain || !enabled} />
                <Label htmlFor="allowPasswordLogin" className="text-sm font-medium cursor-pointer">Enable Username & Password login</Label>
              </div>
              <p className="ml-6 text-xs text-muted-foreground -mt-2">Allow email/password sign-in alongside SAML SSO</p>
            </>
          )}

          {!enabled && !config && (
            <div className="flex items-center gap-2 p-3 rounded-lg border border-border bg-muted/30 text-sm text-muted-foreground">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              Check "Enable SAML SSO" above to configure your Identity Provider settings
            </div>
          )}

          {(enabled || config) && (
            <div className="border-t border-border pt-4 space-y-4">
              <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Identity Provider</h4>
              <div className="space-y-1.5">
                <Label htmlFor="idp-entity-id" className="text-xs">Identity Provider Issuer *</Label>
                <Input id="idp-entity-id" type="text" value={idpEntityId} onChange={(e) => setIdpEntityId(e.target.value)} placeholder="https://sts.windows.net/..." disabled={isPublicDomain || !enabled} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="idp-sso-url" className="text-xs">SAML 2.0 Endpoint (HTTP) *</Label>
                <Input id="idp-sso-url" type="text" value={idpSsoUrl} onChange={(e) => setIdpSsoUrl(e.target.value)} placeholder="https://login.microsoftonline.com/.../saml2" disabled={isPublicDomain || !enabled} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="idp-certificate" className="text-xs">Public Certificate *</Label>
                <Textarea id="idp-certificate" value={idpCertificate} onChange={(e) => setIdpCertificate(e.target.value)} placeholder="-----BEGIN CERTIFICATE-----" rows={6} disabled={isPublicDomain || !enabled} className="font-mono text-xs" />
              </div>
            </div>
          )}

          {(enabled || config) && (
            <div className="border-t border-border pt-4 space-y-4">
              <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Advanced</h4>
              <div className="space-y-1.5">
                <Label htmlFor="button-label" className="text-xs">Sign-in Button Label</Label>
                <Input id="button-label" type="text" value={buttonLabel} onChange={(e) => setButtonLabel(e.target.value)} placeholder="Sign in with SSO" disabled={isPublicDomain || !enabled} />
              </div>
            </div>
          )}

          {(enabled || config) && (
            <div className="flex justify-end pt-2">
              <Button onClick={handleSave} disabled={saving || isPublicDomain || !organization || (enabled && (!idpEntityId.trim() || !idpSsoUrl.trim() || !idpCertificate.trim()))} size="sm">
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {saving ? "Saving..." : config ? "Update Configuration" : "Save Configuration"}
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Setup Instructions */}
      <div className="p-4 rounded-lg border border-border bg-muted/30">
        <p className="text-sm font-medium text-foreground mb-2">Setup Instructions</p>
        <ol className="text-xs text-muted-foreground space-y-1.5 list-decimal list-inside">
          <li>Obtain IdP metadata from your SAML provider (Azure AD, Okta, etc.)</li>
          <li>Copy the Entity ID, SSO URL, and X.509 certificate</li>
          <li>Paste them in the form above and enable SAML SSO</li>
          <li>Users with emails from your configured domains will see SSO option during login</li>
        </ol>
      </div>

      {/* SAML Group Mappings Section */}
      {organization && (
        <div className="mt-6">
          <SAMLGroupMappings
            organization={organization}
            workspaces={workspaces}
          />
        </div>
      )}

      {/* Domain Ownership Warning Dialog */}
      <Dialog
        open={!!domainOwnershipWarning}
        onOpenChange={(open) => !open && cancelAddDomain()}
      >
        <DialogContent>
          <DialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-yellow-100">
                <AlertTriangle className="h-6 w-6 text-yellow-600" />
              </div>
              <DialogTitle>Domain Ownership Warning</DialogTitle>
            </div>
            <DialogDescription>
              You are adding <strong>{domainOwnershipWarning?.domain}</strong>{" "}
              but your verified email is from{" "}
              <strong>{domainOwnershipWarning?.userDomain}</strong>.
              <br />
              <br />
              You should only add domains that you control. Adding domains you
              don't own is a security violation.
              <br />
              <br />
              Are you sure you want to add{" "}
              <strong>{domainOwnershipWarning?.domain}</strong>?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={cancelAddDomain} variant="outline">
              Cancel
            </Button>
            <Button
              onClick={confirmAddDomain}
              className="bg-yellow-600 hover:bg-yellow-700 text-white"
            >
              Yes, Add Domain
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Domain Confirmation Dialog */}
      <Dialog
        open={!!deletingDomain}
        onOpenChange={(open) => !open && cancelDeleteDomain()}
      >
        <DialogContent>
          <DialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-100">
                <AlertTriangle className="h-6 w-6 text-red-600" />
              </div>
              <DialogTitle>Delete Email Domain</DialogTitle>
            </div>
            <DialogDescription>
              Are you sure you want to delete{" "}
              <strong>{deletingDomain?.name}</strong>?
              <br />
              <br />
              Users with @{deletingDomain?.name} email addresses will no longer
              be able to use SSO to sign in.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={cancelDeleteDomain} variant="outline">
              Cancel
            </Button>
            <Button
              onClick={confirmDeleteDomain}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete Domain
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
